import React, { useCallback, useEffect } from 'react';
import { useAppDispatch } from 'app/store';
import {
  fetchInvitesEffect,
  fetchNotificationsEffect,
  fetchUnseenInviteCountEffect,
  fetchUnseenNotificationCountEffect,
} from 'app/store/Notifications/effects';
import { LoadingState } from 'types/enums';
import { InactiveInviteDialog } from 'app/components/Notifications/InactiveInviteDialog';
import {
  useFetchMoreInvites,
  useMarkNotificationAsOld,
  useNotificationAsRead,
  useNotificationsComponentData,
  useRespondToInvite,
} from 'app/components/Notifications/hooks';
import { NotificationsComponent } from './NotificationsComponent';

export const Notifications = () => {
  const dispatch = useAppDispatch();
  const {
    showLoader,
    notifications,
    invites,
    notificationsLoadingState,
    invitesLoadingState,
    originalUnseenNotifications,
    unseenNotificationsCount,
    originalUnseenInvites,
    unseenInvitesCount,
    isLoadingMoreInvites,
    showInactiveInviteDialog,
    invalidInviteMessage,
    moreInvitesLink,
    setOriginalUnseenNotifications,
    setOriginalUnseenInvites,
    setInvalidInviteMessage,
    setShowInactiveDialog,
  } = useNotificationsComponentData();

  const { respondToInvite } = useRespondToInvite({
    setShowInactiveDialog,
    setInvalidInviteMessage,
  });
  const { markNotificationAsRead } = useNotificationAsRead();
  const { markNotificationAsOld } = useMarkNotificationAsOld();
  const { fetchMoreInvites } = useFetchMoreInvites();

  const fetchNotificationsWithCount = useCallback(async () => {
    try {
      await dispatch(fetchNotificationsEffect({}));
      const counts = await dispatch(
        fetchUnseenNotificationCountEffect()
      ).unwrap();
      setOriginalUnseenNotifications(counts.newNotificationsCount);
    } catch (e) {
      // proceed
    }
  }, [dispatch, setOriginalUnseenNotifications]);

  const fetchInvitesWithCount = useCallback(async () => {
    try {
      await dispatch(fetchInvitesEffect({}));
      const count = await dispatch(fetchUnseenInviteCountEffect()).unwrap();
      setOriginalUnseenInvites(count);
    } catch (e) {
      // proceed
    }
  }, [dispatch, setOriginalUnseenInvites]);

  useEffect(() => {
    if (unseenNotificationsCount > originalUnseenNotifications) {
      fetchNotificationsWithCount();
    } else if (unseenNotificationsCount < originalUnseenNotifications) {
      setOriginalUnseenNotifications(unseenNotificationsCount);
    }
  }, [
    originalUnseenNotifications,
    unseenNotificationsCount,
    dispatch,
    fetchNotificationsWithCount,
    setOriginalUnseenNotifications,
  ]);

  useEffect(() => {
    if (unseenInvitesCount > originalUnseenInvites) {
      fetchInvitesWithCount();
    } else if (unseenInvitesCount < originalUnseenInvites) {
      setOriginalUnseenInvites(unseenInvitesCount);
    }
  }, [
    originalUnseenInvites,
    unseenInvitesCount,
    dispatch,
    fetchInvitesWithCount,
    setOriginalUnseenInvites,
  ]);

  useEffect(() => {
    if (notificationsLoadingState === LoadingState.PRISTINE) {
      fetchNotificationsWithCount();
    }
  }, [dispatch, fetchNotificationsWithCount, notificationsLoadingState]);

  useEffect(() => {
    if (invitesLoadingState === LoadingState.PRISTINE) {
      fetchInvitesWithCount();
    }
  }, [invitesLoadingState, dispatch, fetchInvitesWithCount]);

  return (
    <>
      <NotificationsComponent
        notifications={notifications}
        loading={showLoader}
        invites={invites}
        moreInvitesLink={moreInvitesLink}
        isLoadingMoreInvites={isLoadingMoreInvites}
        respondToInvite={respondToInvite}
        fetchMoreInvites={fetchMoreInvites}
        markNotificationAsRead={markNotificationAsRead}
        markNotificationAsOld={markNotificationAsOld}
      />
      <InactiveInviteDialog
        open={showInactiveInviteDialog}
        close={() => setShowInactiveDialog(false)}
        message={invalidInviteMessage}
      />
    </>
  );
};
